import {
  Card,
  CardBody,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import Moment from "moment";
import "moment-timezone";
import { useMemo, useState, React, useEffect, useContext } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import { FaCheckCircle } from "react-icons/fa";
import { BsFillXCircleFill } from "react-icons/bs";
import LoadingOverlay from "react-loading-overlay";
import { ToastContainer, toast } from "react-toastify";
import { MainContext } from "context/MainContext.js";
LoadingOverlay.propTypes = undefined;
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import { useTranslation } from "react-i18next";

const DeviceList = () => {
  const history = useHistory();
  const location = useLocation();
  const privileges = useContext(MainContext);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["devices.gam.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }

    setFilterButtonsOptions([
      { value: "status||all", label: "All Devices" },
      { value: "status||online", label: "Online Devices" },
      { value: "status||offline", label: "Offline Devices" },
    ]);

    fetchFilters();
  }, []);

  const [params, setParams] = useState(location.state?.value ?? "");

  const openDevicePortal = (serialNumber) => {
    fetch("/api/v1/device/url", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
        serialNumber: serialNumber,
        virtuosoPort: window.location.port,
      },
    })
      .then((response) => response.json())
      .then((actualData) => {
        window.open(actualData.data.url, "_blank", "noreferrer");
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const removeDevice = (serialNumber) => {
    setShowLoading(true);
    fetch("/api/v1/device/delete", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        serialNumber: serialNumber,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        setShowLoading(false);
        if (actualData.error_code) {
          toast.error("A problem occured while removing the device", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
        } else {
          toast.success("Device removed successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
          setForceRefreshCount((prevValue) => prevValue + 1);
        }
      })
      .catch((err) => {
        setShowLoading(false);
        console.log(err.message);
      });
  };

  const redirectToEndpointList = (serialNumber) => {
    const location = {
      pathname: "/admin/endpoints/list",
      state: { value: "serialNumber||" + serialNumber },
    };
    history.push(location);
  };

  const redirectToSubscriberList = (serialNumber) => {
    const location = {
      pathname: "/admin/subscribers/list",
      state: { value: "serialNumber||" + serialNumber },
    };
    history.push(location);
  };

  const redirectToPortList = (serialNumber) => {
    const location = {
      pathname: "/admin/ports/list",
      state: { value: "serialNumber||" + serialNumber },
    };
    history.push(location);
  };

  const redirectToConfigurationList = (serialNumber) => {
    const location = {
      pathname: "/admin/configurations/list",
      state: { serialNumber: serialNumber },
    };
    history.push(location);
  };

  const redirectToDeviceDetails = (serialNumber) => {
    const location = {
      pathname: "/admin/devices/details",
      state: {
        value: serialNumber,
      },
    };
    history.push(location);
  };

  const isDeviceActive = (
    lastAnnouncement,
    deviceActiveDelay,
    serverTimeSettings,
  ) => {
    var serverTime = new Date().getTime();
    if (serverTimeSettings) {
      serverTime = new Date(serverTimeSettings).getTime();
    }

    var delay = (serverTime - new Date(lastAnnouncement).getTime()) / 60000;

    if (delay <= deviceActiveDelay) {
      return true;
    }
    return false;
  };

  const fetchFilters = () => {
    setShowLoading(true);
    fetch("/api/v1/device/filters", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
    })
      .then((response) => response.json())
      .then((actualData) => {
        setShowLoading(false);

        // Groups
        actualData.data.groups.map((group) =>
          setFilterButtonsOptions((prevOptions) => [
            ...prevOptions,
            {
              value: group.key,
              label: group.val,
            },
          ]),
        );

        // Sites
        actualData.data.sites.map((site) =>
          setFilterButtonsOptions((prevOptions) => [
            ...prevOptions,
            {
              value: site.key,
              label: site.val,
            },
          ]),
        );
      })
      .catch((err) => {
        setShowLoading(false);
        console.log(err.message);
      });
  };

  const [filterButtonsOptions, setFilterButtonsOptions] = useState([]);

  const filterButtons = useMemo(() => {
    let defaultValue = filterButtonsOptions.find(
      (c) => String(c.value) === String(params),
    );

    if (defaultValue == undefined) {
      defaultValue = filterButtonsOptions[0];
    }

    if (
      params &&
      params != "" &&
      !params.includes("status") &&
      !params.includes("group") &&
      !params.includes("site")
    ) {
      return "";
    } else {
      return (
        <Select
          className="react-select primary"
          classNamePrefix="react-select"
          name="filterButton"
          value={defaultValue}
          onChange={(option) => {
            setParams(option.value);
            setForceRefreshCount((prevValue) => prevValue + 1);
          }}
          isHidden="true"
          options={filterButtonsOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
        />
      );
    }
  });

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        sortBy: "lastAnnouncement",
        Cell: (props) => {
          const tooltipId = `vmElement-${props.row.original.serialNumber}`;

          // Get the delay in minutes between now and the last seen date
          var serverTime = new Date().getTime();
          if (props.settings.server_time) {
            serverTime = new Date(props.settings.server_time).getTime();
          }

          var delay = Math.floor(
            (serverTime -
              new Date(props.row.original.lastAnnouncement).getTime()) /
              60000,
          );

          var icon = null;
          var value = null;

          if (delay >= props.settings.device_minutes_considered_active) {
            icon = (
              <BsFillXCircleFill className="text-danger tableIconFontSize" />
            );
            value = "Offline";
          } else if (props.row.original.hasCommunicationProblem) {
            icon = <FaCheckCircle className="text-warning tableIconFontSize" />;
            value = "Has Communication Problem";
          } else {
            icon = <FaCheckCircle className="text-primary tableIconFontSize" />;
            value = "Online";
          }
          return (
            <>
              <a id={tooltipId} className="vmElement">
                {icon}
              </a>
              <ReactTooltip
                anchorSelect={`#${tooltipId}`}
                place="top"
                style={{ textAlign: "left", zIndex: "100" }}
              >
                <div>{value}</div>
              </ReactTooltip>
            </>
          );
        },
        flex: "0 1 5%",
      },
      {
        Header: "System Name",
        accessor: "name",
        sortBy: "name",
        Cell: (props) => {
          return (
            <a
              onClick={() =>
                redirectToDeviceDetails(props.row.original.serialNumber)
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Serial Number",
        accessor: "serialNumber",
        sortBy: "serialNumber",
        flex: "1 1 0",
      },
      {
        Header: "IP Address",
        accessor: "ipAddress",
        sortBy: "ipAddressNum",
        flex: "1 1 0",
      },
      {
        Header: "MAC",
        accessor: "macAddress",
        sortBy: "macAddress",
        flex: "1 1 0",
      },
      {
        Header: "Model",
        accessor: "productClass",
        sortBy: "productClass",
        flex: "1 1 0",
      },
      {
        Header: "Ports",
        accessor: "ports",
        sortBy: "ports",
        Cell: (props) => {
          return (
            <a
              onClick={() =>
                redirectToPortList(props.row.original.serialNumber)
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Endpoints",
        accessor: "endpoints",
        sortBy: "endpoints",
        Cell: (props) => {
          return (
            <a
              onClick={() =>
                redirectToEndpointList(props.row.original.serialNumber)
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Subscribers",
        accessor: "subscribers",
        sortBy: "subscribers",
        Cell: (props) => {
          return (
            <a
              onClick={() =>
                redirectToSubscriberList(props.row.original.serialNumber)
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Bandwidths",
        accessor: "bandwidths",
        sortBy: "bandwidths",
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Groups",
        accessor: "groups",
        sortBy: "groups",
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Uptime",
        accessor: "uptime",
        sortBy: "uptime_num",
        flex: "1 1 0",
      },
      {
        Header: "ASY",
        accessor: "asy",
        sortBy: "hardwareVersion",
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Revision",
        accessor: "revision",
        sortable: false,
        show: false,
        flex: "1 1 0",
      },
      {
        Header: "Last Seen",
        accessor: "lastAnnouncement",
        sortBy: "lastAnnouncement",
        Cell: (props) => {
          // Get the delay in minutes between now and the last seen date
          var serverTime = new Date().getTime();
          if (props.settings.server_time) {
            serverTime = new Date(props.settings.server_time).getTime();
          }

          var delay = Math.floor(
            (serverTime - new Date(props.value).getTime()) / 60000,
          );

          // Set the right background color base on the delay or the status
          var color = "";
          if (delay >= props.settings.device_minutes_considered_active) {
            color = "rgba(232, 80, 77, 1)"; // Muted Red
          } else if (delay >= props.settings.device_minutes_warning_active) {
            color = "rgba(240, 205, 102, 1)"; // Soft Yellow
          }
          return (
            <div style={{ backgroundColor: color, width: "100%" }}>
              {props.settings.general_convert_date_with_user_timezone === "true"
                ? Moment(props.value)
                    .tz(props.settings.user_timezone)
                    .format("yy-MM-DD hh:mm a")
                : Moment.utc(props.value).format("yy-MM-DD hh:mm a")}
            </div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Software",
        accessor: "softwareVersion",
        sortBy: "softwareVersion",
        Cell: (props) => {
          // Get the delay in minutes between now and the last seen date
          var serverTime = new Date().getTime();
          if (props.settings.server_time) {
            serverTime = new Date(props.settings.server_time).getTime();
          }

          var delay = Math.floor(
            (serverTime -
              new Date(props.row.original.lastAnnouncement).getTime()) /
              60000,
          );

          var color = "";
          if (
            props.row.original.firmwareUpgradeInProgress &&
            delay < props.settings.device_minutes_considered_active
          ) {
            color = "rgba(149, 179, 215, 1)"; // Light Steel Blue
          } else if (props.extraData.includes(props.row.original.name)) {
            color = "lightgreen";
          }

          return (
            <div style={{ backgroundColor: color, width: "100%" }}>
              {props.value}
            </div>
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "Firmware Upgrade In Progress",
        accessor: "firmwareUpgradeInProgress",
        sortBy: "firmwareUpgradeInProgress",
        show: false,
        filterable: false,
        disableExport: true,
        flex: "1 1 0",
      },
      {
        Header: "Has Communication Problem",
        accessor: "hasCommunicationProblem",
        sortBy: "hasCommunicationProblem",
        show: false,
        filterable: false,
        disableExport: true,
        flex: "1 1 0",
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (props) => {
          return (
            <UncontrolledDropdown direction="down">
              <DropdownToggle
                caret
                color="info"
                style={{
                  padding: "6px 14px 6px 14px",
                  margin: "0px",
                }}
              >
                Actions
              </DropdownToggle>
              <DropdownMenu container={"body"}>
                {!isDeviceActive(
                  props.row.original.lastAnnouncement,
                  props.settings.device_minutes_considered_active,
                  props.settings.server_time,
                ) && Utilities.hasAccess(privileges["devices.delete"]) ? (
                  <>
                    <DropdownItem
                      onClick={() =>
                        removeDevice(props.row.original.serialNumber)
                      }
                    >
                      Remove Device
                    </DropdownItem>
                    <DropdownItem divider />
                  </>
                ) : (
                  ""
                )}

                {Utilities.hasAccess(privileges["devices.details.display"]) ? (
                  <DropdownItem
                    onClick={() =>
                      redirectToDeviceDetails(props.row.original.serialNumber)
                    }
                  >
                    View Details
                  </DropdownItem>
                ) : (
                  ""
                )}

                <DropdownItem
                  onClick={() =>
                    redirectToConfigurationList(props.row.original.serialNumber)
                  }
                >
                  View GAM Configurations
                </DropdownItem>

                {sessionStorage
                  .getItem("permissions")
                  .includes("GAM_PORTAL_ACCESS") ? (
                  <>
                    <DropdownItem divider />
                    <DropdownItem
                      onClick={() =>
                        openDevicePortal(props.row.original.serialNumber)
                      }
                    >
                      Open GAM Portal
                    </DropdownItem>
                  </>
                ) : (
                  ""
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  return (
    <>
      <ToastContainer />
      <div className="content">
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading"
          className={showLoading ? "loading" : ""}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  {params &&
                  params.includes("site") &&
                  params.includes("siteview") ? (
                    <BreadcrumbItem>
                      <NavLink
                        to={{
                          pathname: "/admin/installations/site/list",
                        }}
                      >
                        {t(
                          "sites.breadcrumb.sites.t." +
                            sessionStorage.getItem("bundleType"),
                          "Sites",
                        )}
                      </NavLink>
                      <i>&lt;{params.split("||")[1]}&gt;</i>
                    </BreadcrumbItem>
                  ) : (
                    ""
                  )}
                  <BreadcrumbItem>GAM</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <ReactTable
                  query="/api/v1/device/list"
                  columns={columns}
                  param={params}
                  title="Devices"
                  filterButtons={filterButtons}
                  forceRefresh={forceRefreshCount}
                  className="-striped -highlight primary-pagination"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default DeviceList;
